import {DepotCidSrv, JDepotCidResult, ODepotCidRequest} from  "/@lib@/store/cid.js";
import {JNodeInfos, UrlTreeSrv} from  "/@lib@/store/urlTree.js";
import {IDepotCtx, IDepotResCtx, IDepotShortResCtx, InfoUpdatePendingRes, JCidMetas, JPersistMetas} from  "/@lib@/store/res.js";
import {ActionMenu, EButtonUiContext} from  "/@lib@/commons/actions.js";
import {IReg, IRegPointer, REG} from  "/@lib@/commons/registry.js";
import {ERROR} from  "/@lib@/core/errorReport.js";
import {POPUP} from  "/@back@/commons/widgets/popups.js";
import {JUser, USER} from  "/@lib@/core/user.js";
import {DepotUniverse, IDepotEnv, IDepotResEnv, IDepotResUiEnv, ODepotLCConfig} from  "/@lib@/store/depot.js";
import {ActionBtn} from  "/@back@/commons/widgets/buttons.js";
import {JSX} from  "/@lib@/commons/xml/dom.js";
import {LIFECYCLE} from  "/@lib@/wsp/lcTask.js";
import {UserSelector} from  "/@back@/core/dialogs/userSelector.js";
import {DepotResAction} from  "/@lib@/store/actions.js";
import type {ResEditAction} from  "/@back@/store/actions/resActions.js";
import DEFAULT_STATE = LIFECYCLE.DEFAULT_STATE;

export class DepotLcProv {
	static readonly LC_KEY = "lc";

	static readonly STATE_UNKNOWN_KEY = "?";

	static readonly STATE_NONE_KEY = "";

	protected _transitions: Array<DepotLcTransition>;
	protected _states: Map<String, DepotLcState>;
	protected _cidSrv: DepotCidSrv;
	protected _adminUT: UrlTreeSrv;

	get label(): string {return this.conf?.label || "Cycle de vie"}

	get transitionsGroupOrder(): string {return this.conf?.transitionsGroupOrder || "*"}

	constructor(protected readonly universe?: DepotUniverse, private conf?: ODepotLCConfig) {
		this._states = new Map<String, DepotLcState>();
		this._transitions = [];
		if (universe) {
			this._adminUT = universe.adminUrlTree;
			this._cidSrv = universe.cid;
		}
	}

	/**
	 * Surcharge de la configuration
	 * TODOsc7 : à supprimer
	 * @deprecated
	 */
	configure(adminTree: UrlTreeSrv, cidServer: DepotCidSrv) {
		this._cidSrv = cidServer;
		this._adminUT = adminTree;
	}

	async executeTransition(path: string, transitionKey: string, ctx: IDepotShortResCtx | IDepotResCtx | IDepotCtx, uiCtx: Node): Promise<JDepotCidResult> {
		const transition = this.getTransition(transitionKey);
		let {ResEditAction} = await import("/@back@/store/actions/resActions.js");
		return ResEditAction.sendCidUpdate({
			olderPath: path,
			lc: transition.code
		}, ctx, uiCtx, ['scCidSessStatus']);
	}

	addTransition(tr: DepotLcTransition | ODepotLcTransitionConfig) {
		const transition = tr instanceof DepotLcTransition ? tr : new DepotLcTransition(tr);
		if (!transition.reg) transition.reg = this.universe?.reg;
		const i = this.getTransitionIndex(transition.code);
		if (i == null) this._transitions.push(transition);
		else this._transitions[i] = transition;
	}

	getTransition(key: string): DepotLcTransition {
		const i = this.getTransitionIndex(key);
		return i == null ? null : this._transitions[i];
	}

	getTransitionIndex(key: string): number {
		for (let i = 0; i < this._transitions.length; i++) {
			if (this._transitions[i].code == key) return i;
		}
		return null;
	}

	addState(st: DepotLcState | ODepotLcStateConfig) {
		const state = st instanceof DepotLcState ? st : new DepotLcState(st);
		if (!state.reg) state.reg = this.universe?.reg;
		this._states.set(state.code, state);
	}

	getState(key: string): DepotLcState {
		if (!key) {
			let noneState = this._states.get(DepotLcProv.STATE_NONE_KEY);
			if (!noneState) {
				noneState = new DepotLcState({code: DepotLcProv.STATE_NONE_KEY, label: "non défini"})
				this._states.set(DepotLcProv.STATE_NONE_KEY, noneState);
			}
			return noneState;
		}
		const state = this._states.get(key);
		if (state) {
			return state;
		} else {
			let unknownState = this._states.get(DepotLcProv.STATE_UNKNOWN_KEY);
			if (!unknownState) {
				unknownState = new DepotLcState({code: DepotLcProv.STATE_UNKNOWN_KEY, label: "indéterminé"})
				this._states.set(DepotLcProv.STATE_UNKNOWN_KEY, unknownState);
			}
			return unknownState;
		}
	}

	listAvailableTransitions(state: string, prc: string): Array<DepotLcTransition> {
		let availbleTr = new Array<DepotLcTransition>();
		for (let i = 0; i < this._transitions.length; i++) {
			if (this._transitions[i].isValidTrans(state, prc)) availbleTr.push(this._transitions[i]);
		}
		return availbleTr;
	}
}

export interface ODepotLcTransitionConfig {
	code: string
	label: string
	restrictFromStates: Array<string>
	prcWhiteList: Array<string>
	allowFromAllPrcs: boolean
	to: DepotLcState | string
	showPerms?: Array<string>
	enablePerms?: Array<string>
	icon?: string
	iconUrl?: string // URL résolue dynamiquement à partir de 'icon'
	sortKey?: number
	group?: string
	isTransitionVisible?: (reg: IReg<IDepotResEnv>) => boolean
	isTransitionEnabled?: (reg: IReg<IDepotResEnv>) => boolean
	controlBeforeExecute?: (reg: IReg<IDepotResEnv>) => Promise<boolean>
}

export class DepotLcTransition {
	reg?: IReg<IDepotEnv>

	get code(): string {return this.conf.code}

	get label(): string {return this.conf.label}

	get iconUrl(): string {return (this.conf.icon && this.reg) ? this.reg.env.resolver.resolve(this.conf.icon).url : this.conf.iconUrl}

	get sortKey(): number {return this.conf.sortKey || 0}

	get group(): string | null {return this.conf.group}

	get restrictFromStates(): Array<string> | null {return this.conf.restrictFromStates}

	get to(): string {return (this.conf.to instanceof DepotLcState) ? this.conf.to.code : this.conf.to}

	get prcWhiteList(): Array<string> {return this.conf.prcWhiteList || []}

	get allowFromAllPrcs(): boolean {return "allowFromAllPrcs" in this.conf ? this.conf.allowFromAllPrcs : false}

	get showPerms(): Array<string> | null {return this.conf.showPerms}

	get enablePerms(): Array<string> | null {return this.conf.enablePerms}

	get isTransitionVisible(): (reg: IReg<IDepotResEnv>) => boolean {return this.conf.isTransitionVisible}

	get isTransitionEnabled(): (reg: IReg<IDepotResEnv>) => boolean {return this.conf.isTransitionEnabled}

	get controlBeforeExecute(): (reg: IReg<IDepotResEnv>) => Promise<boolean> {return this.conf.controlBeforeExecute}

	constructor(private conf: ODepotLcTransitionConfig) {}

	isValidTrans(state: string, prc: string): boolean {
		if (this.restrictFromStates?.indexOf(state) === -1) return false;
		if (this.allowFromAllPrcs === false && this.prcWhiteList.indexOf(prc) === -1) return false;
		return true;
	}
}

export interface ODepotLcStateConfig {
	code: string
	label?: string
	icon?: string
	iconUrl?: string // URL résolue dynamiquement à partir de 'icon'
	//color?: string
}

export class DepotLcState {
	reg?: IReg<IDepotEnv>

	private conf: ODepotLcStateConfig

	get code(): string {return this.conf.code}

	get label(): string {return this.conf.label}

	get iconUrl(): string {return (this.conf.icon && this.reg) ? this.reg.env.resolver.resolve(this.conf.icon).url : this.conf.iconUrl}

	constructor(init: ODepotLcStateConfig);
	constructor(init: string | ODepotLcStateConfig, label?: string, icon?: string)/* constructor deprecated*/ {
		if (typeof init === "object") {
			this.conf = init;
		} else {
			this.conf = {
				code: init,
				label,
				icon
			}
		}
	}

}

/**
 * Menu des transitions exécutables d'une ressource du depot.
 *
 * Action à embarquer en dehors d'un framework Wui Depot
 * (référence explicite à depotlcProv et récup du nodeInfos).
 */
export class DepotResListTransition<C extends IDepotResCtx> extends ActionMenu<C> {

	constructor(protected init?: ODepotResListTransitionInit) {
		super('itemListTransition');
		this._description = "Changer d'état...";
		this._group = "transitions";
	}

	getLabel(ctx: C): string {
		const ni = ctx.reg.env.nodeInfos;
		const state = ni ? ctx.reg.env.lifecycle.getState(ni.metas.lc) : null;
		return state ? state.label : "Chargement en cours...";
	}

	getIcon(ctx: C): string {
		const ni = ctx.reg.env.nodeInfos;
		const state = ni ? ctx.reg.env.lifecycle.getState(ni.metas.lc) : null;
		return state ? state.iconUrl : null;
	}

	getGroupOrder(ctx: C): string {
		return ctx.reg.env.lifecycle.transitionsGroupOrder || super.getGroupOrder(ctx);
	}

	isVisible(ctx: C): boolean {
		if (!ctx.reg.env.lifecycle) return false;
		const ni = ctx.reg.env.nodeInfos;
		const state = ni ? ctx.reg.env.lifecycle.getState(ni.metas?.lc) : null;
		// Si état "" ou indéterminé et pas de transition => concept de transition masqué pour le noeud
		if (state.code === DEFAULT_STATE && !ctx.reg.env.lifecycle.listAvailableTransitions(ctx.reg.env.nodeInfos?.metas?.lc, ctx.reg.env.nodeInfos.prc).length)
			return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: C): boolean {
		if (!super.isEnabled(ctx)) return false;
		for (const action of this.getActions(ctx)) {
			if (action.isEnabled(ctx)) return true;
		}
		return false;
	}

	buildCustomButton(ctx: C, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		const btn = new DepotResListLcTransitionActBtn().initialize({
			reg: REG.findReg(parent, ctx),
			action: this,
			actionContext: ctx,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			uiContext: uiContext,
			role: uiContext === 'menu' ? 'menuitem' : 'button',
		});
		if (this.init?.hideLcLabel !== true) btn.lcMainTitle = ctx.reg.env.lifecycle.label;
		btn._hideLabel = this.init?.hideStateLabel === true;
		return btn;
	}

	getActions(ctx: C): ResExecTransition<C>[] {
		const ni = ctx.reg.env.nodeInfos;
		const state = ni ? ctx.reg.env.lifecycle.getState(ni.metas.lc) : null;
		let list: ResExecTransition<C>[] = [];
		if (ctx.reg.env.nodeInfos == null) return [];
		if (!state) return [];
		let transitions = ctx.reg.env.lifecycle.listAvailableTransitions(state.code, ctx.reg.env.nodeInfos.prc);
		if (transitions != null) {
			transitions = transitions.sort((a, b) => a.sortKey - b.sortKey);
			for (const tr of transitions.values()) list.push(new ResExecTransition(tr));
		}
		return list;
	}
}

interface ODepotResListTransitionInit {
	/** Si true, les labels des states seront masqués; false par défaut*/
	hideStateLabel: boolean

	/** Si true, le titre du LC sera masqué du bounton; false par défaut*/
	hideLcLabel: boolean
}

class DepotResListLcTransitionActBtn extends ActionBtn<IRegPointer<IDepotResUiEnv>> {

	protected _lcMainTitle: string;

	protected _lcMainTitleElt: HTMLElement;

	protected _initialize(init: any) {
		const reg = this.findReg(init);
		super._initialize(init);
		reg.installSkin("store-res-btn-lc-transitions", this.shadowRoot);
	}

	set lcMainTitle(val: string) {
		this._lcMainTitle = val;
		if (!this._lcMainTitleElt) {
			this._lcMainTitleElt = this.shadowRoot.appendChild(<div class="smallTi">{val}</div>);
			const labelCtn = this.shadowRoot.appendChild(<div class='ctn'/>);
			if (!this._labelElt) this._createLabel();
			if (!this._iconElt) this._createIcon();
			if (this._iconElt) labelCtn.appendChild(this._iconElt);
			if (this._labelElt) labelCtn.appendChild(this._labelElt);
		}
	}

}

REG.reg.registerSkin('store-res-btn-lc-transitions', 1, /* language=CSS */ `
	:host {
		display: flex;
		flex-direction: column;
	}

	:host([role=menu]:not([disabled])) {
		background-position-y: 1.5rem;
	}

	.ctn {
		display: flex;
	}

	.label {
		color: var(--alt2-color);
		font-weight: bold;
		padding: 0 0.3em;
	}

	.smallTi {
		color: var(--fade-color);
		font-style: italic;
		font-size: 85%;
		padding-block-end: 0.2rem;
	}
`);
customElements.define('store-res-btn-lc-transitions', DepotResListLcTransitionActBtn);


/**
 * Action d'exécution d'une transition
 */
export class ResExecTransition<C extends IDepotResCtx> extends DepotResAction<C> {

	constructor(public trans: DepotLcTransition, overrideExec?: (ctx: C, ev?: Event) => any) {
		super(trans.code);
		if (trans.showPerms) this.requireResVisiblePerm(trans.showPerms);
		if (trans.enablePerms) this.requireResEnabledPerm(trans.enablePerms);
		this._label = trans.label;
		this._icon = trans.iconUrl;
		this._group = trans.group;
		if (overrideExec) this.execute = overrideExec;
	}

	isVisible(ctx: C): boolean {
		if (this.trans.isTransitionVisible && this.trans.isTransitionVisible(ctx.reg) === false) return false;
		return super.isVisible(ctx);
	}

	isEnabled(ctx: C): boolean {
		if (this.trans.isTransitionEnabled && this.trans.isTransitionEnabled(ctx.reg) === false) return false;
		return super.isEnabled(ctx);
	}

	async execute(ctx: C, ev?: Event) {
		if (this.trans.controlBeforeExecute && !await this.trans.controlBeforeExecute(ctx.reg)) return;
		const promise = new Promise<JDepotCidResult & Pick<JPersistMetas, 'resId' | 'path'>>(async (resolve, reject) => {
			try {
				const preActions = ctx.reg.mergeLists<(transition: ResExecTransition<C>) => boolean | Promise<boolean>>("lc:transitions:pre:actions", "lc:transitions:" + this._id + ":pre:actions");
				if (preActions) for (const preAction of preActions) {
					const preActionRes = preAction(this);
					const result = preActionRes instanceof Promise ? await preActionRes : preActionRes;
					if (result === false) return;
				}
				resolve(ctx.reg.env.lifecycle.executeTransition(ctx.reg.env.path, this._id, ctx, ctx.emitter));
			} catch (e) {
				reject(e);
			}
		});

		let {ResEditAction} = await import("/@back@/store/actions/resActions.js");
		ResEditAction.getInfoBroker(ctx)?.dispatchInfo(new InfoUpdatePendingRes(ctx.reg.env.path, promise), ctx.emitter);
		//const resp = await new MsgOver().setCustomMsg("Veuillez patienter...", "info").showMsgOver(ctx.emitter).waitFor(promise);
		const resp = await promise;
		if (resp == null)
			return ERROR.reportError({msg: "Une erreur est survenue durant le changement d'état."});
	}
}

/**
 * Affichage/modification **d'une seule Resp**
 * Usage free uniquement dans des projets depotres. Deprecated au profit de {@link EditRespsRes} ?
 */
export class ChangeUserResp<C extends IRegPointer<IDepotResUiEnv>> extends DepotResAction<C> {

	constructor(code: string, overrideExec?: (ctx: never, ev?: Event) => any) {
		super(code);
		if (overrideExec) this.execute = overrideExec;
	}

	buildCustomButton(ctx: C, uiContext: EButtonUiContext, parent?: Element): Element | null | undefined {
		return new ChangeUserRespBtn().initialize({
			reg: REG.findReg(parent, ctx),
			action: this,
			actionContext: ctx,
			skin: this.getSkin(ctx),
			skinOver: this.getSkinOver(ctx),
			uiContext: uiContext,
			role: uiContext === 'menu' ? 'menuitem' : 'button'
		});
	}

	async execute(ctx: C, ev?: Event) {
		//const self = DOMSH.findHost<TaskFieldUsers>(this);
		const {UserSelector} = await import("/@back@/core/dialogs/userSelector.js");
		const usersRaw: string[] = ctx.reg.env.nodeInfos.metas[this._id];
		const userSelector = new UserSelector().initialize({
			reg: ctx.reg,
			userGrid: {
				usersSrv: ctx.reg.env.universe.useUsers,
				grid: {selType: "multi", skinOver: "userRespSelector"},
				filterTypeInputVisibility: true,
			},
			selectAndCloseOnDblClick: true,
			startSel: usersRaw != null && usersRaw.length ? await ctx.reg.env.universe.useUsers.getUserSet(usersRaw) : []
		});
		const users = await POPUP.showMenuFromEvent<JUser[]>(userSelector, ev || ctx.reg.env.uiRoot, ctx.reg.env.uiRoot, null, {initWidth: '20em'}).onNextClose();
		if (users) {
			let usersAr = [];
			for (const user of users) usersAr.push(user.account);
			const req: ODepotCidRequest<JCidMetas, JDepotCidResult> = {
				cidMetas: {
					olderPath: ctx.reg.env.path,
				},
				returnProps: ['scCidSessStatus']
			};
			req.cidMetas[this._id] = JSON.stringify(usersAr);
			const resp = await ctx.reg.env.universe.cid.syncSend(req);
			if (resp.scCidSessStatus !== "commited") return ERROR.reportError({msg: "Une erreur est survenue durant le changement de responsabilité."});
		}
	}
}

REG.reg.registerSkin('userRespSelector', 1, /* language=CSS */`
.inSel {
	background-color: var(--row-inSel-bgcolor);
}
`);


class ChangeUserRespBtn extends ActionBtn<IRegPointer<IDepotResUiEnv>> {
	_hideLabel = false;

	connectedCallback() {
		if (this.actionContext.reg.env.nodeInfos !== null) this.onResChange(this.actionContext.reg.env.nodeInfos);
		super.connectedCallback();
		const lstn = this.actionContext.reg.env.nodeInfosChange;
		if (lstn) lstn.on("nodeChange", (this._onResChange = this.onResChange.bind(this)));
		else this.actionContext.reg.env.nodeInfosPending?.then((ni: JNodeInfos) => {this.onResChange(ni)});
	}

	disconnectedCallback() {
		if (this._onResChange) {
			this.actionContext.reg.env.nodeInfosChange.removeListener("nodeChange", this._onResChange);
			this._onResChange = null;
		}
	}

	async onResChange(niNew: JNodeInfos) {
		if (niNew) {
			let label = this.action.getLabel(this.actionContext);
			const users = niNew.metas[this.action.getId()];
			if (users && users.length > 0) {
				label += USER.getPrimaryName(await this.actionContext.reg.env.universe.useUsers.getUser(users[0]));
				if (users.length > 1) label += "...";
			} else label = "Aucun";
			this.label = label;
		} else {
			this.label = "";
		}
	}

	protected _onResChange: (niNew: JNodeInfos, niOld: JNodeInfos) => void;
}

customElements.define('store-resp-changeuser', ChangeUserRespBtn);